import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { errorToUserMessage } from "../services/errorMessages";

const TAG_COLORS = [
  "#22C55E", "#3B82F6", "#EF4444", "#EAB308", "#8B5CF6",
  "#EC4899", "#F97316", "#06B6D4", "#6B7280", "#14B8A6",
];

export default function SurveyTagManager({ campaignId, onTagsChange }) {
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", color: "" });
  const [loading, setLoading] = useState(true);

  const loadTags = useCallback(async () => {
    if (!campaignId) return;
    try {
      const res = await api.get(`/survey-tags/campaigns/${campaignId}/tags`);
      setTags(res.data.tags || []);
      onTagsChange?.(res.data.tags || []);
    } catch (err) {
      console.error("Failed to load tags:", err);
    } finally {
      setLoading(false);
    }
  }, [campaignId, onTagsChange]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTagName.trim()) return;

    try {
      await api.post(`/survey-tags/campaigns/${campaignId}/tags`, {
        name: newTagName.trim(),
        color: newTagColor,
      });
      setNewTagName("");
      setNewTagColor(TAG_COLORS[(tags.length + 1) % TAG_COLORS.length]);
      loadTags();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to create tag"));
    }
  };

  const handleUpdate = async (tagId) => {
    try {
      await api.put(`/survey-tags/tags/${tagId}`, editForm);
      setEditing(null);
      loadTags();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to update tag"));
    }
  };

  const handleDelete = async (tagId) => {
    const tag = tags.find((t) => t.id === tagId);
    if (!window.confirm(`Delete tag "${tag?.name}"? This will remove it from all contacts.`)) return;

    try {
      await api.delete(`/survey-tags/tags/${tagId}`);
      loadTags();
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to delete tag"));
    }
  };

  if (loading) {
    return <div className="animate-pulse h-20 bg-gray-100 rounded-lg" />;
  }

  return (
    <div className="space-y-3">
      {/* Existing tags */}
      <div className="space-y-2">
        {tags.map((tag) => (
          <div key={tag.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
            {editing === tag.id ? (
              <div className="flex items-center space-x-2 flex-1">
                <input
                  type="text"
                  className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  autoFocus
                />
                <div className="flex space-x-1">
                  {TAG_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setEditForm({ ...editForm, color: c })}
                      className={`w-5 h-5 rounded-full border-2 ${editForm.color === c ? "border-gray-800" : "border-transparent"}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <button onClick={() => handleUpdate(tag.id)} className="text-xs text-blue-600 font-medium">Save</button>
                <button onClick={() => setEditing(null)} className="text-xs text-gray-400">Cancel</button>
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                  <span className="text-sm font-medium text-gray-700">{tag.name}</span>
                  {tag.response_count !== undefined && (
                    <span className="text-xs text-gray-400">({tag.response_count})</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => { setEditing(tag.id); setEditForm({ name: tag.name, color: tag.color }); }}
                    className="text-xs text-gray-400 hover:text-blue-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(tag.id)}
                    className="text-xs text-gray-400 hover:text-red-600"
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Create new tag */}
      <form onSubmit={handleCreate} className="flex items-center space-x-2">
        <input
          type="text"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
          placeholder="New tag name..."
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
        />
        <div className="flex space-x-1">
          {TAG_COLORS.slice(0, 5).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setNewTagColor(c)}
              className={`w-5 h-5 rounded-full border-2 ${newTagColor === c ? "border-gray-800" : "border-transparent"}`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        <button
          type="submit"
          disabled={!newTagName.trim()}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          Add Tag
        </button>
      </form>
    </div>
  );
}
